import React, { FC, useEffect, useMemo, useState } from "react";
import { Drawer, Menu } from "antd";
import type { MenuProps } from "antd";
import {
  ProjectOutlined,
  FileOutlined,
  CloseOutlined,
  // DashboardOutlined / CalendarOutlined / CreditCardOutlined / ClockCircleOutlined
  // are only needed by the hidden Dashboard / Calendar / Rate Cards / Pending
  // Approvals items below.
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { setMobileMenuOpen } from "../../store/slices/layout/layoutSlice";
import {
  SIDE_NAV,
  findNavMatch,
  makeNavHandlers,
} from "../sidebar/sideNavItems";
import Logo from "../logo/Logo";

type MenuItem = Required<MenuProps>["items"][number];

// The project sidebar is hidden below 992px (project-sidebar.css), so this
// drawer is the ONLY way to reach a project's pages on a phone. Keys are
// prefixed so they can never collide with the global menu keys below.
const PNAV = "pnav-";

const MobileNavigation: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { mobileMenuOpen, currentProjectId, currentProjectName } = useSelector(
    (state: RootState) => state.layout,
  );

  const handleClose = () => {
    dispatch(setMobileMenuOpen(false));
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    handleClose();
  };

  const projectMatch = useMemo(
    () => (currentProjectId ? findNavMatch(location.pathname) : null),
    [currentProjectId, location.pathname],
  );

  // Get selected keys based on current path
  const getSelectedKeys = (): string[] => {
    // A project page wins over the global "Projects" entry.
    if (projectMatch) return [`${PNAV}${projectMatch.selectedKey}`];

    const path = location.pathname;
    if (path.includes("/main-dashboard")) return ["dashboard"];
    if (
      path.includes("/pms/projects") ||
      path.includes("/pms/project") ||
      path.includes("/active-projects") ||
      path.includes("/draftProjects") ||
      path.includes("/closed-projects") ||
      path.includes("/archived-projects") ||
      path.includes("/inactive-projects") ||
      path.includes("/groups-projects") ||
      path.includes("/recycle-bin")
    )
      return ["projects"];
    if (path.includes("/Checklist")) return ["checklists"];
    if (path.includes("/calendar")) return ["calendar"];
    if (path.includes("/rate-cards")) return ["ratecards"];
    if (path.includes("/manpower-norms")) return ["norms-manpower"];
    if (path.includes("/machinery-norms")) return ["norms-machinery"];
    if (path.includes("/norms")) return ["norms-material"];
    if (path.includes("/assets")) return ["assets-master"];
    if (path.includes("/pending-approvals")) return ["pending"];
    return [];
  };

  // The drawer stays mounted between opens, so openKeys is controlled rather
  // than defaultOpenKeys (which would go stale after the first navigation).
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  useEffect(() => {
    if (projectMatch?.openKey) {
      setOpenKeys([`${PNAV}${projectMatch.openKey}`]);
      return;
    }
    if (location.pathname.includes("/norms")) setOpenKeys(["norms"]);
    else if (location.pathname.includes("/assets")) setOpenKeys(["assets"]);
    else setOpenKeys([]);
  }, [projectMatch, location.pathname]);

  const navHandlers = useMemo(
    () =>
      makeNavHandlers(
        navigate,
        currentProjectId,
        location.state,
        currentProjectName,
      ),
    [navigate, currentProjectId, location.state, currentProjectName],
  );

  // Project-level pages, from the same config the desktop sidebar renders.
  const projectItems: MenuItem[] = useMemo(() => {
    if (!currentProjectId) return [];

    const go = (item: {
      route?: string;
      expenseRoute?: string;
      absolutePath?: string;
    }) => {
      navHandlers.navigateItem(item);
      handleClose();
    };

    return [
      {
        key: `${PNAV}group`,
        type: "group",
        label: currentProjectName || "Project",
        children: SIDE_NAV.map((group) =>
          group.children?.length
            ? {
                key: `${PNAV}${group.key}`,
                icon: group.icon,
                label: group.label,
                children: group.children.map((child) => ({
                  key: `${PNAV}${child.key}`,
                  label: child.label,
                  onClick: () => go(child),
                })),
              }
            : {
                key: `${PNAV}${group.key}`,
                icon: group.icon,
                label: group.label,
                onClick: () => go(group),
              },
        ),
      },
      { type: "divider" },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProjectId, currentProjectName, navHandlers]);

  // Keep in sync with MAIN_MENU (mainMenuItems.ts). Dashboard, Checklists,
  // Calendar, Rate Cards and Pending Approvals are hidden from the menu; routes still work.
  const globalItems: MenuItem[] = [
    // {
    //   key: "dashboard",
    //   icon: <DashboardOutlined />,
    //   label: "Dashboard",
    //   onClick: () => handleNavigate("/pms/main-dashboard"),
    // },
    {
      key: "projects",
      icon: <ProjectOutlined />,
      label: "Projects",
      onClick: () => handleNavigate("/pms/projects"),
    },
    // {
    //   key: "checklists",
    //   icon: <FileOutlined />,
    //   label: "Checklists",
    //   onClick: () => handleNavigate("/pms/Checklist/checklist-lists"),
    // },
    // {
    //   key: "calendar",
    //   icon: <CalendarOutlined />,
    //   label: "Calendar",
    //   onClick: () => handleNavigate("/pms/calendar"),
    // },
    // {
    //   key: "ratecards",
    //   icon: <CreditCardOutlined />,
    //   label: "Rate Cards",
    //   onClick: () => handleNavigate("/pms/rate-cards"),
    // },
    {
      key: "norms",
      icon: <FileOutlined />,
      label: "Norms",
      children: [
        {
          key: "norms-material",
          label: "Material",
          onClick: () => handleNavigate("/pms/norms"),
        },
        {
          key: "norms-manpower",
          label: "Manpower",
          onClick: () => handleNavigate("/pms/manpower-norms"),
        },
        {
          key: "norms-machinery",
          label: "Machinery",
          onClick: () => handleNavigate("/pms/machinery-norms"),
        },
      ],
    },
    {
      key: "assets",
      icon: <FileOutlined />,
      label: "Assets",
      children: [
        {
          key: "assets-master",
          label: "Asset Master",
          onClick: () => handleNavigate("/pms/assets"),
        },
      ],
    },
    // {
    //   key: "pending",
    //   icon: <ClockCircleOutlined />,
    //   label: "Pending Approvals",
    //   onClick: () => handleNavigate("/pms/pending-approvals"),
    // },
  ];

  return (
    <Drawer
      title={null}
      placement="left"
      onClose={handleClose}
      open={mobileMenuOpen}
      width={300}
      closeIcon={null}
      styles={{
        body: {
          padding: 0,
        },
        header: {
          display: "none",
        },
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 20px",
          borderBottom: "1px solid #e4e9f0",
          background: "#fff",
        }}
      >
        <Logo />
        <button
          onClick={handleClose}
          aria-label="Close menu"
          style={{
            border: "none",
            background: "#f0f5fb",
            borderRadius: "8px",
            width: "36px",
            height: "36px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <CloseOutlined style={{ color: "#2557d6", fontSize: "16px" }} />
        </button>
      </div>

      {/* Menu */}
      <div style={{ padding: "12px 0" }}>
        <Menu
          mode="inline"
          selectedKeys={getSelectedKeys()}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          items={[...projectItems, ...globalItems]}
          style={{
            border: "none",
          }}
        />
      </div>
    </Drawer>
  );
};

export default MobileNavigation;
